import { useState } from "react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { DateInput } from "shared/components/forms/DateInput";
import {
  CalendarMinus02Icon,
  CalendarRemove02Icon,
  Calendar01Icon,
  CalendarDownload02Icon,
  Calendar03Icon,
  ArrowDown01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import moment from "moment";
import DatePickerComp from "react-datepicker";
import { Card } from "shared/components/Card";

import { DueDateFilterOptions, CreatedAtOptions } from "../types";
import {
  DropdownLabels,
  timeLineSection,
  CreatedAtButtonConfig,
  DueFilterButtonConfig,
  DueFilterMapping,
  CreatedAtFilterMapping,
  noOffsetDate
} from "../constants";
import { useMedia } from "shared/providers";

const CreatedAtIcon = (iconName: string): IconSvgElement => {
  const iconMap: Record<string, IconSvgElement> = {
    CalendarMinus02Icon,
    Calendar01Icon,
    CalendarDownload02Icon,
    Calendar03Icon
  };
  return iconMap[iconName];
};

const DueIcon = (iconName: string): IconSvgElement => {
  const iconMap: Record<string, IconSvgElement> = {
    CalendarMinus02Icon,
    CalendarRemove02Icon,
    Calendar01Icon,
    CalendarDownload02Icon,
    Calendar03Icon
  };
  return iconMap[iconName];
};

type FilterKey = "due" | "created_at";

type DateFilterSectionProps<K extends string> = {
  filterKey: FilterKey;
  kind: K | null;
  inputValues: { start_date: string; end_date: string };
  onChange: (
    kind: K,
    inputValues: { start_date: string; end_date: string }
  ) => void;
};

const getConfig = (filterKey: FilterKey) => {
  if (filterKey === "due")
    return {
      mapping: DueFilterMapping,
      buttonConfig: DueFilterButtonConfig,
      getIcon: DueIcon,
      label: DropdownLabels.due,
      customKind: DueDateFilterOptions.CUSTOM,
      noneKind: DueDateFilterOptions.NONE,
      overduekind: DueDateFilterOptions.OVERDUE
    };

  return {
    mapping: CreatedAtFilterMapping,
    buttonConfig: CreatedAtButtonConfig,
    getIcon: CreatedAtIcon,
    label: timeLineSection.created_at,
    customKind: CreatedAtOptions.CUSTOM,
    noneKind: CreatedAtOptions.NONE,
    overduekind: null
  };
};

export const DateFilterSection = <K extends string>({
  filterKey,
  kind,
  inputValues,
  onChange
}: DateFilterSectionProps<K>) => {
  const config = getConfig(filterKey);
  const { isMobile } = useMedia();
  const [datePickerDates, setDatePickerDates] = useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: moment(inputValues.start_date).toDate(),
    endDate: moment(inputValues.end_date).toDate()
  });
  const [openDropdown, setOpenDropdown] = useState(false);
  const [openCustom, setOpenCustom] = useState(false);
  const [tempInputValues, setTempInputValues] = useState<{
    start_date: string;
    end_date: string;
  }>({
    start_date: inputValues.start_date,
    end_date: inputValues.end_date
  });

  const handleStartDateChange = (value: string) => {
    setTempInputValues((prev) => ({
      ...prev,
      start_date: value
    }));

    if (value && value.length === 10)
      setDatePickerDates((prev) => ({
        ...prev,
        startDate: moment(value).toDate()
      }));
  };

  const handleEndDateChange = (value: string) => {
    setTempInputValues((prev) => ({
      ...prev,
      end_date: value
    }));

    if (value && value.length === 10)
      setDatePickerDates((prev) => ({
        ...prev,
        endDate: moment(value).toDate()
      }));
  };

  const handleDatePickerChange = (dates: [Date | null, Date | null]) => {
    const [startDate, endDate] = dates;

    setDatePickerDates({
      startDate: startDate as Date,
      endDate: endDate as Date
    });

    if (startDate && endDate)
      setTempInputValues({
        start_date: noOffsetDate(startDate),
        end_date: noOffsetDate(endDate)
      });
  };

  const getLabelColor = (): string => {
    if (!kind) return "text-content-100";
    if (config.overduekind && kind == config.overduekind)
      return "text-danger-500";
    return "text-content-1000";
  };

  const getDisplayLabel = (): string => {
    if (!kind) return "Select date range...";
    if (kind == config.customKind)
      return `${inputValues.start_date} - ${inputValues.end_date}`;
    const matchingConfig = config.buttonConfig.find((cfg) => cfg.key == kind);
    return matchingConfig?.label ?? "Select date range...";
  };
  return (
    <>
      <div className="border-b border-alpha-40 w-[328px] text-center -mx-2" />
      <div className="space-y-4">
        <span className="font-bold text-sm text-content-1000">
          {config.label}
        </span>
        <div className="relative w-full">
          <Button
            variant={ButtonVariant.BASE}
            borderLess
            size={ButtonSize.BASE}
            onClick={() => {
              setOpenDropdown((v) => !v);
            }}
            className="w-full rounded-lg border border-alpha-100 py-3 px-[14px] shadow-xs !justify-start !gap-2"
          >
            <span className={cx("text-base font-medium", getLabelColor())}>
              {getDisplayLabel()}
            </span>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              className={cx(
                "absolute right-3 top-1/2 -translate-y-1/2 text-content-1000 transition-all",
                openDropdown ? "rotate-180" : ""
              )}
              size={16}
              strokeWidth={1.5}
            />
          </Button>
          <Show if={openDropdown}>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpenDropdown(false)}
            />
            <Card className="absolute top-[calc(100%+8px)] max-w-[240px] w-full z-40 overflow-hidden shadow-xl">
              {config.buttonConfig.map((cfg, idx) => (
                <Button
                  key={cfg.key}
                  onClick={() => {
                    onChange(cfg.key as K, inputValues);
                    if (cfg.key === config.customKind) {
                      setTempInputValues({
                        start_date: inputValues.start_date,
                        end_date: inputValues.end_date
                      });
                      setOpenCustom(true);
                    }
                    setOpenDropdown(false);
                  }}
                  variant={ButtonVariant.BASE}
                  borderLess
                  size={ButtonSize.SM}
                  className={cx(
                    "w-full !justify-start !py-2.5 !min-h-[51px] !gap-3 !rounded-none",
                    idx !== config.buttonConfig.length - 1 &&
                      "!border-b !border-alpha-40",
                    cfg.key === config.noneKind
                      ? "!text-content-200"
                      : cfg.textColor || "!text-content-1000"
                  )}
                  HugeIcon={config.getIcon(cfg.icon)}
                >
                  <span className="text-base font-medium">{cfg.label}</span>
                </Button>
              ))}
            </Card>
          </Show>
        </div>
      </div>

      <Show if={openCustom}>
        <Card
          className={cx(
            "bg-white z-40 fixed",
            isMobile
              ? "max-w-[calc(100%-28px)] w-full h-auto left-1/2 -translate-x-1/2 -translate-y-2"
              : "w-[1000px] h-[470px] top-1/2 bottom-1/2 right-1/2 -translate-x-1/4 -translate-y-1/2"
          )}
        >
          <div className="flex-1 w-full overflow-auto">
            <div className="flex flex-col lg:flex-row gap-[14px] border-b border-alpha-40 lg:items-center px-4 py-6">
              <DateInput
                id={`start_date_${filterKey}`}
                hideOptional
                label="From"
                value={tempInputValues.start_date}
                onChange={(e) => handleStartDateChange(e.target.value)}
              />
              <p className="hidden lg:block">-</p>
              <DateInput
                id={`end_date_${filterKey}`}
                hideOptional
                label="To"
                value={tempInputValues.end_date}
                onChange={(e) => handleEndDateChange(e.target.value)}
              />
            </div>
            <div className="lg:block hidden">
              <DatePickerComp
                selected={datePickerDates.startDate}
                onChange={handleDatePickerChange}
                monthsShown={2}
                startDate={datePickerDates.startDate}
                endDate={datePickerDates.endDate}
                selectsRange
                inline
              />
            </div>
          </div>
          <div className="border-t border-alpha-40 px-6 py-4 flex gap-2 justify-end">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.BASE}
              onClick={() => {
                setOpenCustom(false);
              }}
              className="max-w-[103px] w-full shadow-xs !py-2.5 !px-4"
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.BASE}
              onClick={() => {
                if (kind)
                  onChange(kind, {
                    start_date: tempInputValues.start_date,
                    end_date: tempInputValues.end_date
                  });
                setOpenCustom(false);
              }}
              className="max-w-[103px] w-full shadow-xs !py-2.5 !px-4"
            >
              Apply
            </Button>
          </div>
        </Card>
      </Show>
    </>
  );
};
